import { useState } from 'react';
import { usePreviewToolKinds, useSetToolKinds } from './useToolConfigs';
import type { InvocationShapeType, ToolKindsPreview } from '../../../types/backend';

/**
 * A kind mark change, previewed before it is applied: the preview names the active agents that lose the tool, and
 * applying sends exactly those agents, so the server refuses it if they changed meanwhile. `ticked` is the tool's
 * automation tick as read now, which the preview is compared with.
 */
export function useKindChange(actionId: string, ticked: boolean) {
  const previewKinds = usePreviewToolKinds();
  const setKinds = useSetToolKinds();
  const [pending, setPending] = useState<ToolKindsPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const preview = async (kinds: InvocationShapeType[] | null) => {
    setError(null);
    try {
      setPending(await previewKinds.mutateAsync({ action_id: actionId, kinds }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preview failed');
    }
  };

  const apply = async () => {
    if (!pending) return;
    try {
      await setKinds.mutateAsync({
        action_id: actionId, kinds: pending.kinds, expected_removals: pending.removals.map((r) => r.agent_id),
      });
      setPending(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Change failed');
      setPending(null);
    }
  };

  // A reset (kinds null) keeps the tick.
  const tickAfter = pending ? (pending.kinds === null ? ticked : pending.kinds.includes('batch')) : ticked;

  return {
    pending,
    error,
    preview,
    apply,
    cancel: () => setPending(null),
    busy: previewKinds.isPending || setKinds.isPending,
    applying: setKinds.isPending,
    blocking: pending?.removals.filter((r) => r.left_without_tools) ?? [],
    consulted: pending?.consulted_by_automation ?? [],
    ticking: pending !== null && tickAfter && !ticked,
    unticking: pending !== null && !tickAfter && ticked,
  };
}
